import { auth } from "./firebase-config.js";
import { detectPlatform, passTypeForPlatform } from "./utils/platform.js";

// Shared base URL + auth token for all Cloud Function calls. The handlers
// that previously held their own copies (`onboardingHandler.js`,
// `employeeHandler.js`) import from here so we have a single source of truth.
export const CLOUD_FUNCTIONS_BASE_URL =
  "https://us-central1-independentloyalist.cloudfunctions.net";
export const CLOUD_FUNCTIONS_AUTH_TOKEN =
  "Bearer E946B986-46E1-4A9F-BA41-68B3815D604B";

const androidFunctionUrl = `${CLOUD_FUNCTIONS_BASE_URL}/generatedAndroidPass`;
const downloadIOSPassUrl = `${CLOUD_FUNCTIONS_BASE_URL}/downloadIOSPass`;

// Content types that prove a response is NOT a pass, however healthy its
// status code looks. Deliberately a denylist of the human/error formats rather
// than an allowlist of "application/vnd.apple.pkpass": the failure this needs
// to catch is a 200 carrying an HTML or JSON error page, and an allowlist would
// additionally reject every enrolment if the server ever labelled the pass
// application/octet-stream or application/zip. Safari sniffs the pass itself,
// so the exact binary type is the server's business, not ours.
const NON_PASS_CONTENT_TYPES = ["text/html", "application/json", "text/plain"];

// Generation is normally sub-second, but the customer must never be left
// staring at a spinner if the function hangs. On timeout we give up on
// confirming and fall back to the old behaviour rather than block delivery.
const IOS_PASS_CONFIRM_TIMEOUT_MS = 15000;

// Builds the iOS pass download URL per the shared API contract:
//   GET /downloadIOSPass?idToken=<freshFirebaseIdToken>&businessId=<id>
// The server verifies the token and derives the customer email from it, so we
// never send the email. The returned URL is navigated to top-level on iOS,
// which makes Safari overlay the "Add to Apple Wallet" sheet.
export const buildIOSPassUrl = async (businessId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user available to build the iOS pass URL");
  }
  // No forced refresh: the token was minted by the sign-in moments ago, and
  // the SDK refreshes a cached token on its own when it is within 30s of
  // expiry, so a forced refresh only added a round trip to the token endpoint.
  const idToken = await user.getIdToken(false);
  const params = new URLSearchParams({ idToken, businessId });
  return `${downloadIOSPassUrl}?${params.toString()}`;
};

// Warms the pass function this device will hit on submit (GET ?warm=1, see
// lulu-backend handlers/warm-up.js). Fired when the enrolment form renders,
// while the customer is still typing, so the function instance and its image
// caches are ready by the time they tap "Sign in & enrol". Best-effort and
// fire-and-forget: the response is opaque and ignored, and nothing waits on it.
export const warmPassFunction = (businessId) => {
  if (!businessId || typeof fetch !== "function") return;
  const target =
    passTypeForPlatform(detectPlatform()) === "android"
      ? androidFunctionUrl
      : downloadIOSPassUrl;
  const params = new URLSearchParams({ warm: "1", businessId });
  try {
    fetch(`${target}?${params.toString()}`, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
    }).catch(() => {});
  } catch (e) {
    // Never let a warm-up failure affect the page.
  }
};

// Confirms the server can actually build the pass BEFORE we hand the URL to a
// top-level navigation. Without this, building the iOS URL is pure string
// concatenation that resolves instantly, so the loading indicator stops before
// generation has even started and a generation failure surfaces as a broken
// navigation instead of the retry screen.
//
// Three outcomes, and the difference matters:
//   - returns true   the server returned a real .pkpass, generation succeeded.
//   - returns false  the probe could not run at all (CORS, offline, timeout).
//                    We have learned nothing, so callers must carry on exactly
//                    as they did before rather than break a flow that works.
//   - throws         the server gave a definitive failure, which is a genuine
//                    generation error worth showing the customer.
//
// Cost: the pass is built twice server-side, once here and once on navigation.
// That is the deliberate trade for a truthful loading state until the backend
// grows a `prepareIOSPass` endpoint that builds once and returns a short-lived
// download URL - at which point this function collapses into awaiting that one
// call and the double build disappears.
const confirmIOSPassIsBuildable = async (passUrl) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    IOS_PASS_CONFIRM_TIMEOUT_MS
  );

  let response;
  try {
    response = await fetch(passUrl, {
      method: "GET",
      signal: controller.signal,
    });
  } catch (error) {
    // AbortError (our timeout) and TypeError (network failure, or a missing
    // CORS header on an endpoint that until now was only ever navigated to)
    // both say nothing about whether the pass would have built. Inconclusive.
    return false;
  } finally {
    clearTimeout(timeoutId);
  }

  // Only the status and headers matter here, so drop the payload rather than
  // make the customer download a pass body we are about to discard.
  try {
    await response.body?.cancel();
  } catch (e) {
    // Body already settled or unsupported - nothing to release.
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: iOS pass generation failed`);
  }

  // A 200 carrying an error page is still a failure. Content-Type is
  // CORS-safelisted so it is readable; a missing one is unknowable rather than
  // wrong, so it passes.
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (NON_PASS_CONTENT_TYPES.some((type) => contentType.includes(type))) {
    throw new Error(`Unexpected iOS pass content type: ${contentType}`);
  }

  return true;
};

// Builds the iOS pass URL and waits for the server to prove it can produce the
// pass. Callers get back a URL that is safe to navigate to plus whether we
// positively confirmed it, so the await genuinely spans generation.
export const prepareIOSPass = async (businessId) => {
  const passUrl = await buildIOSPassUrl(businessId);
  const verified = await confirmIOSPassIsBuildable(passUrl);
  return { passUrl, verified };
};

// `email` is accepted for signature compatibility/logging but is intentionally
// NOT sent to the server - the email is derived from the verified ID token.
export const generateIOSRewardsCard = async (email, businessId) => {
  const { passUrl, verified } = await prepareIOSPass(businessId);
  return { passType: "ios", passUrl, verified };
};

// Single branding-save endpoint for the dashboard Settings page. The backend
// regenerates the stale stamp ("circle") images AND updates the Google Wallet
// loyalty class in one call, so the frontend no longer deletes stamps or calls
// the class-update endpoint separately. Use this whenever anything on the
// "Brand & rewards card" tab changed.
export const updateSettingsBranding = async (payload) => {
  const response = await fetch(
    `${CLOUD_FUNCTIONS_BASE_URL}/updateSettingsBranding`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: CLOUD_FUNCTIONS_AUTH_TOKEN,
      },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`HTTP ${response.status}: ${body}`);
  }
  return response.json().catch(() => ({}));
};

export const generateAndroidRewardsCard = async (qrCodeData) => {
  try {
    const response = await fetch(androidFunctionUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qrCodeData }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`HTTP ${response.status}: ${body}`);
    }

    const { saveToWalletUrl } = await response.json();

    // Only trust a Google Wallet save URL on the expected origin before we
    // hand it to a real navigation.
    let parsed;
    try {
      parsed = new URL(saveToWalletUrl);
    } catch (e) {
      throw new Error("Invalid Google Wallet save URL returned");
    }
    if (parsed.origin !== "https://pay.google.com") {
      throw new Error("Unexpected Google Wallet save URL origin");
    }

    return { passType: "android", passUrl: saveToWalletUrl };
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("Network error or timeout occurred");
    } else {
      throw error;
    }
  }
};